import React, { useCallback, useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  create_exclusive_content,
  create_subscription_plan,
  delete_exclusive_content,
  delete_subscription_plan,
  link_plan_content,
  list_exclusive_contents,
  list_subscription_plans,
  unlink_plan_content,
  update_exclusive_content,
  update_subscription_plan,
} from '../../../utils/services';
import './PinesCupones.css';
import './ContenidoExclusivoT8.css';

function formatPrice(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return value || '0.00';
  return `$${num.toFixed(2)}`;
}

function ContenidoExclusivoT8({ token }) {
  const navigate = useNavigate();
  const sessionToken = token || localStorage.getItem('token');

  const [tab, setTab] = useState('contents');
  const [contents, setContents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  const [showContentModal, setShowContentModal] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [contentName, setContentName] = useState('');
  const [savingContent, setSavingContent] = useState(false);

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planName, setPlanName] = useState('');
  const [planDescription, setPlanDescription] = useState('');
  const [planMonthly, setPlanMonthly] = useState('');
  const [planAnnual, setPlanAnnual] = useState('');
  const [planContentIds, setPlanContentIds] = useState([]);
  const [savingPlan, setSavingPlan] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [managePlan, setManagePlan] = useState(null);
  const [linkContentId, setLinkContentId] = useState('');
  const [linkingContent, setLinkingContent] = useState(false);

  const showToast = useCallback((message, variant = 'success') => {
    setToastVariant(variant);
    setToastMessage(message);
  }, []);

  useEffect(() => {
    if (!toastMessage) return undefined;
    const t = setTimeout(() => setToastMessage(''), 5000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  const fetchContents = useCallback(async () => {
    const res = await list_exclusive_contents(sessionToken);
    if (res === 'INVALID') { navigate('/'); return []; }
    const list = Array.isArray(res) ? res : [];
    setContents(list);
    return list;
  }, [sessionToken, navigate]);

  const fetchPlans = useCallback(async () => {
    const res = await list_subscription_plans(sessionToken);
    if (res === 'INVALID') { navigate('/'); return []; }
    const list = Array.isArray(res) ? res : [];
    setPlans(list);
    return list;
  }, [sessionToken, navigate]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchContents(), fetchPlans()]);
    } finally {
      setLoading(false);
    }
  }, [fetchContents, fetchPlans]);

  useEffect(() => {
    if (!sessionToken) { navigate('/'); return; }
    refreshAll();
  }, [sessionToken, navigate, refreshAll]);

  const openCreateContent = () => {
    setEditingContent(null);
    setContentName('');
    setShowContentModal(true);
  };

  const openEditContent = (content) => {
    setEditingContent(content);
    setContentName(content.name || '');
    setShowContentModal(true);
  };

  const handleSaveContent = async () => {
    if (savingContent || !contentName.trim()) return;
    setSavingContent(true);
    try {
      const res = editingContent
        ? await update_exclusive_content(sessionToken, editingContent.id, contentName.trim())
        : await create_exclusive_content(sessionToken, contentName.trim());
      if (res === 'INVALID') { navigate('/'); return; }
      if (res?.error) {
        showToast(res.message || 'Error al guardar el contenido', 'danger');
        return;
      }
      showToast(editingContent ? 'Contenido actualizado' : 'Contenido creado');
      setShowContentModal(false);
      await fetchContents();
    } catch (err) {
      showToast('Error al guardar el contenido', 'danger');
    } finally {
      setSavingContent(false);
    }
  };

  const openCreatePlan = () => {
    setEditingPlan(null);
    setPlanName('');
    setPlanDescription('');
    setPlanMonthly('');
    setPlanAnnual('');
    setPlanContentIds([]);
    setShowPlanModal(true);
  };

  const openEditPlan = (plan) => {
    setEditingPlan(plan);
    setPlanName(plan.name || '');
    setPlanDescription(plan.description || '');
    setPlanMonthly(plan.price_monthly ?? '');
    setPlanAnnual(plan.price_annual ?? '');
    setPlanContentIds([]);
    setShowPlanModal(true);
  };

  const togglePlanContent = (id) => {
    setPlanContentIds((prev) => (
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    ));
  };

  const handleSavePlan = async () => {
    if (savingPlan || !planName.trim()) return;
    setSavingPlan(true);
    try {
      const res = editingPlan
        ? await update_subscription_plan(sessionToken, {
            id: editingPlan.id,
            name: planName.trim(),
            description: planDescription.trim(),
            price_monthly: planMonthly,
            price_annual: planAnnual,
          })
        : await create_subscription_plan(sessionToken, {
            name: planName.trim(),
            description: planDescription.trim(),
            price_monthly: planMonthly,
            price_annual: planAnnual,
            content_ids: planContentIds,
          });
      if (res === 'INVALID') { navigate('/'); return; }
      if (res?.error) {
        showToast(res.message || 'Error al guardar el plan', 'danger');
        return;
      }
      showToast(editingPlan ? 'Plan actualizado' : 'Plan creado');
      setShowPlanModal(false);
      await fetchPlans();
    } catch (err) {
      showToast('Error al guardar el plan', 'danger');
    } finally {
      setSavingPlan(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      const res = deleteTarget.kind === 'plan'
        ? await delete_subscription_plan(sessionToken, deleteTarget.id)
        : await delete_exclusive_content(sessionToken, deleteTarget.id);
      if (res === 'INVALID') { navigate('/'); return; }
      if (res?.error) {
        showToast(res.message || 'Error al eliminar', 'danger');
        return;
      }
      showToast(deleteTarget.kind === 'plan' ? 'Plan eliminado' : 'Contenido eliminado');
      setDeleteTarget(null);
      if (deleteTarget.kind === 'plan') {
        if (managePlan?.id === deleteTarget.id) setManagePlan(null);
        await fetchPlans();
      } else {
        await refreshAll();
      }
    } catch (err) {
      showToast('Error al eliminar', 'danger');
    } finally {
      setDeleting(false);
    }
  };

  const handleLinkContent = async () => {
    if (!managePlan || !linkContentId || linkingContent) return;
    setLinkingContent(true);
    try {
      const res = await link_plan_content(sessionToken, managePlan.id, linkContentId);
      if (res === 'INVALID') { navigate('/'); return; }
      if (res?.error) {
        showToast(res.message || 'Error al vincular el contenido', 'danger');
        return;
      }
      showToast('Contenido vinculado');
      setLinkContentId('');
      const updated = await fetchPlans();
      const refreshed = updated.find((p) => p.id === managePlan.id);
      if (refreshed) setManagePlan(refreshed);
    } catch (err) {
      showToast('Error al vincular el contenido', 'danger');
    } finally {
      setLinkingContent(false);
    }
  };

  const handleUnlinkContent = async (contentId, contentName) => {
    try {
      const res = await unlink_plan_content(sessionToken, managePlan.id, contentId);
      if (res === 'INVALID') { navigate('/'); return; }
      if (res?.error) {
        showToast(res.message || 'Error al desvincular el contenido', 'danger');
        return;
      }
      showToast(`"${contentName}" desvinculado`);
      const updated = await fetchPlans();
      const refreshed = updated.find((p) => p.id === managePlan.id);
      if (refreshed) setManagePlan(refreshed);
      else setManagePlan(null);
    } catch (err) {
      showToast('Error al desvincular el contenido', 'danger');
    }
  };

  const availableContents = contents.filter(
    (c) => !managePlan?.contents?.some((linked) => linked.id === c.id)
  );

  const currentCount = tab === 'contents' ? contents.length : plans.length;

  return (
    <div className="cupones-container">
      <h1 className="cupones-title">Contenido Exclusivo T8</h1>
      <div className="cupones-count">Total: {currentCount}</div>

      <div className="exclusive-tabs">
        <button
          type="button"
          className={`exclusive-tab${tab === 'contents' ? ' is-active' : ''}`}
          onClick={() => setTab('contents')}
        >
          Contenidos
        </button>
        <button
          type="button"
          className={`exclusive-tab${tab === 'plans' ? ' is-active' : ''}`}
          onClick={() => setTab('plans')}
        >
          Planes
        </button>
      </div>

      {tab === 'contents' ? (
        <div className="cupones-table-container">
          <Table hover className="cupones-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Nombre</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="3" className="text-center py-4">Cargando...</td></tr>
              ) : contents.length === 0 ? (
                <tr><td colSpan="3" className="text-center py-4">No hay contenidos exclusivos</td></tr>
              ) : contents.map((content, index) => (
                <tr key={content.id}>
                  <td>{index + 1}</td>
                  <td>{content.name}</td>
                  <td>
                    <div className="cupones-actions">
                      <button type="button" className="btn-cupones-edit" onClick={() => openEditContent(content)}>
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn-cupones-delete"
                        onClick={() => setDeleteTarget({ kind: 'content', id: content.id, name: content.name })}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="cupones-table-container">
          <Table hover className="cupones-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Nombre</th>
                <th>Descripción</th>
                <th>Mensual</th>
                <th>Anual</th>
                <th>Contenidos</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="text-center py-4">Cargando...</td></tr>
              ) : plans.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-4">No hay planes</td></tr>
              ) : plans.map((plan, index) => (
                <tr key={plan.id}>
                  <td>{index + 1}</td>
                  <td><span className="cupones-code">{plan.name}</span></td>
                  <td>
                    <div className="exclusive-description">
                      {plan.description || <span className="cupones-no-products">Sin descripción</span>}
                    </div>
                  </td>
                  <td className="exclusive-price">{formatPrice(plan.price_monthly)}</td>
                  <td className="exclusive-price">{formatPrice(plan.price_annual)}</td>
                  <td>
                    <div className="cupones-products">
                      {plan.contents && plan.contents.length > 0 ? (
                        plan.contents.map((c) => (
                          <span key={c.id} className="cupones-badge">{c.name}</span>
                        ))
                      ) : (
                        <span className="cupones-no-products">Sin contenidos</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="cupones-actions">
                      <button
                        type="button"
                        className="btn-cupones-manage"
                        onClick={() => { setManagePlan(plan); setLinkContentId(''); }}
                      >
                        Gestionar contenidos
                      </button>
                      <button type="button" className="btn-cupones-edit" onClick={() => openEditPlan(plan)}>
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn-cupones-delete"
                        onClick={() => setDeleteTarget({ kind: 'plan', id: plan.id, name: plan.name })}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <button
        type="button"
        className="cupones-fab"
        onClick={tab === 'contents' ? openCreateContent : openCreatePlan}
        aria-label={tab === 'contents' ? 'Crear contenido' : 'Crear plan'}
      >
        +
      </button>

      {showContentModal && (
        <div className="cupones-backdrop" onClick={() => setShowContentModal(false)}>
          <div className="cupones-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cupones-modal-close" onClick={() => setShowContentModal(false)} aria-label="Cerrar">×</button>
            <h2 className="cupones-modal-title">{editingContent ? 'Editar contenido' : 'Nuevo contenido'}</h2>
            <div className="cupones-modal-field">
              <label>Nombre <span className="required">*</span></label>
              <input
                type="text"
                value={contentName}
                onChange={(e) => setContentName(e.target.value)}
                placeholder="Ej: STEM Premium"
                autoFocus
              />
            </div>
            <button
              type="button"
              className="cupones-modal-action"
              onClick={handleSaveContent}
              disabled={savingContent || !contentName.trim()}
            >
              {savingContent ? 'Guardando...' : (editingContent ? 'Guardar' : 'Crear contenido')}
            </button>
          </div>
        </div>
      )}

      {showPlanModal && (
        <div className="cupones-backdrop" onClick={() => setShowPlanModal(false)}>
          <div className="cupones-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cupones-modal-close" onClick={() => setShowPlanModal(false)} aria-label="Cerrar">×</button>
            <h2 className="cupones-modal-title">{editingPlan ? 'Editar plan' : 'Nuevo plan'}</h2>
            <div className="cupones-modal-field">
              <label>Nombre <span className="required">*</span></label>
              <input
                type="text"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder="Ej: Plan Escuela"
                autoFocus
              />
            </div>
            <div className="cupones-modal-field">
              <label>Descripción</label>
              <textarea
                value={planDescription}
                onChange={(e) => setPlanDescription(e.target.value)}
                placeholder="Qué incluye este plan"
              />
            </div>
            <div className="cupones-modal-field">
              <label>Precio mensual <span className="required">*</span></label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={planMonthly}
                onChange={(e) => setPlanMonthly(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="cupones-modal-field">
              <label>Precio anual <span className="required">*</span></label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={planAnnual}
                onChange={(e) => setPlanAnnual(e.target.value)}
                placeholder="0.00"
              />
            </div>
            {!editingPlan && (
              <div className="cupones-modal-field">
                <label>Contenidos (opcional)</label>
                <div className="cupones-product-checklist">
                  {contents.length === 0 ? (
                    <p className="cupones-hint">Aún no hay contenidos activos.</p>
                  ) : contents.map((c) => (
                    <label key={c.id} className="cupones-check-item">
                      <input
                        type="checkbox"
                        checked={planContentIds.includes(c.id)}
                        onChange={() => togglePlanContent(c.id)}
                      />
                      <span>{c.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <button
              type="button"
              className="cupones-modal-action"
              onClick={handleSavePlan}
              disabled={savingPlan || !planName.trim() || planMonthly === '' || planAnnual === ''}
            >
              {savingPlan ? 'Guardando...' : (editingPlan ? 'Guardar' : 'Crear plan')}
            </button>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="cupones-backdrop" onClick={() => { if (!deleting) setDeleteTarget(null); }}>
          <div className="cupones-modal cupones-modal--confirm" onClick={(e) => e.stopPropagation()}>
            <div className="cupones-confirm-icon">!</div>
            <h2 className="cupones-modal-title">
              {deleteTarget.kind === 'plan' ? 'Eliminar plan' : 'Eliminar contenido'}
            </h2>
            <p className="cupones-confirm-text">
              ¿Seguro que quieres eliminar <strong>{deleteTarget.name}</strong>?
            </p>
            <p className="cupones-confirm-warning">
              {deleteTarget.kind === 'plan'
                ? 'El plan se ocultará y se desvincularán sus contenidos. No se borra de forma permanente.'
                : 'El contenido se ocultará y se quitará de los planes. Helpdesk dejará de ofrecerlo.'}
            </p>
            <div className="cupones-confirm-actions">
              <button type="button" className="btn-cupones-cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancelar
              </button>
              <button type="button" className="btn-cupones-confirm-delete" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {managePlan && (
        <div className="cupones-backdrop" onClick={() => setManagePlan(null)}>
          <div className="cupones-modal cupones-modal--manage" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="cupones-modal-close" onClick={() => setManagePlan(null)} aria-label="Cerrar">×</button>
            <h2 className="cupones-modal-title">
              Contenidos — <span className="cupones-modal-code">{managePlan.name}</span>
            </h2>
            <div className="cupones-manage-section">
              <h3 className="cupones-section-label">Vinculados actualmente</h3>
              {managePlan.contents && managePlan.contents.length > 0 ? (
                <ul className="cupones-product-list">
                  {managePlan.contents.map((c) => (
                    <li key={c.id} className="cupones-product-item">
                      <span>{c.name}</span>
                      <button
                        type="button"
                        className="btn-cupones-unlink"
                        onClick={() => handleUnlinkContent(c.id, c.name)}
                        title="Desvincular"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="cupones-hint">Este plan no tiene contenidos vinculados.</p>
              )}
            </div>
            <div className="cupones-manage-section">
              <h3 className="cupones-section-label">Vincular contenido</h3>
              {availableContents.length === 0 ? (
                <p className="cupones-hint">Todos los contenidos activos ya están vinculados.</p>
              ) : (
                <div className="cupones-link-row">
                  <select
                    className="cupones-select"
                    value={linkContentId}
                    onChange={(e) => setLinkContentId(e.target.value)}
                  >
                    <option value="">Seleccionar contenido...</option>
                    {availableContents.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn-cupones-link"
                    onClick={handleLinkContent}
                    disabled={!linkContentId || linkingContent}
                  >
                    {linkingContent ? 'Vinculando...' : 'Vincular'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {toastMessage && (
        <div className={`cupones-toast cupones-toast--${toastVariant}`}>
          <span>{toastMessage}</span>
          <button type="button" className="cupones-toast-close" onClick={() => setToastMessage('')} aria-label="Cerrar">×</button>
        </div>
      )}
    </div>
  );
}

export default ContenidoExclusivoT8;
